import { CpmCheckbox, CpmTooltip } from '../../../../components/design-system';
import { formatFlaggedDate } from '../../../../constants/centralProductMaster.constants';
import type { CpmBarcodeConflictItem } from '../../../../types/centralProductMaster.types';

function storeLabel(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null && 'name' in value) {
        const name = (value as { name?: unknown }).name;
        if (typeof name === 'string') return name;
    }
    return String(value);
}

function numberedTooltipList(names: string[]) {
    if (names.length === 0) return null;
    return (
        <div className="flex flex-col" style={{ gap: 4 }}>
            {names.map((name, i) => (
                <div key={`${name}-${i}`}>
                    {i + 1}. {name}
                </div>
            ))}
        </div>
    );
}

function deriveBarcodeView(row: CpmBarcodeConflictItem) {
    const conflict = row.claimCount > 1;
    const disputed = !!row.disputed;
    const badges: Array<{ label: string; kind: 'conflict' | 'disputed'; hint: string }> = [];
    if (conflict) {
        badges.push({
            label: 'CONFLICTED',
            kind: 'conflict',
            hint: `${row.claimCount} master products are claiming this barcode right now`,
        });
    }
    if (disputed) {
        badges.push({
            label: 'DISPUTED',
            kind: 'disputed',
            hint: 'Store brands that used to report this trusted barcode have stopped',
        });
    }

    const namesForDisplay = (row.entries ?? []).map((e) => e.name?.trim() || '');
    const nonEmptyNames = namesForDisplay.filter((name) => name.length > 0);
    const claimNames =
        nonEmptyNames.slice(0, 2).join(' · ') +
        (nonEmptyNames.length > 2 ? ` · +${nonEmptyNames.length - 2} more` : '');

    const reporterSet = new Set<string>();
    const droppedSet = new Set<string>();
    for (const entry of row.entries ?? []) {
        for (const src of entry.sources ?? []) {
            const label = storeLabel(src.storeBrandName) || storeLabel(src.storeBrand);
            if (label) reporterSet.add(label);
        }
        const removals = Array.isArray(entry.removals) ? entry.removals : [];
        for (const rem of removals) {
            const label =
                rem && typeof rem === 'object'
                    ? storeLabel((rem as { storeBrandName?: unknown }).storeBrandName) ||
                      storeLabel((rem as { storeBrand?: unknown }).storeBrand)
                    : null;
            if (label) droppedSet.add(label);
        }
    }
    const reporters = [...reporterSet];
    const dropped = [...droppedSet];

    return {
        badges,
        conflict,
        claims: conflict
            ? `Claimed by ${row.claimCount} product${row.claimCount === 1 ? '' : 's'}`
            : 'Trusted owner — auto-linking',
        claimNames,
        claimNamesList: nonEmptyNames,
        reports: `Reported by ${row.reportCount} store brand${row.reportCount === 1 ? '' : 's'}`,
        reportNames: reporters.join(', ') || `${row.reportCount} reports`,
        reporterNamesList: reporters,
        hasDropped: dropped.length > 0,
        droppedLabel: `${dropped.length} stopped using it: ${dropped.join(', ')}`,
        sinceLabel: conflict ? 'First conflict' : 'First drop-off',
    };
}

interface BarcodeConflictTableProps {
    items: CpmBarcodeConflictItem[];
    selectedIds: string[];
    onToggle: (row: CpmBarcodeConflictItem) => void;
    onToggleAll: (checked: boolean) => void;
    onOpen: (row: CpmBarcodeConflictItem) => void;
}

export default function BarcodeConflictTable({
    items,
    selectedIds,
    onToggle,
    onToggleAll,
    onOpen,
}: BarcodeConflictTableProps) {
    const allSelected = items.length > 0 && items.every((i) => selectedIds.includes(i.barcode));

    return (
        <div>
            <div
                className="cpm-table-head grid items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)] sticky top-0 z-[14] shadow-[inset_0_-1px_0_var(--cpm-border-default)]"
                style={{
                    gridTemplateColumns:
                        '38px minmax(150px,0.8fr) minmax(0,1.4fr) minmax(0,1.4fr) 152px 26px',
                    gap: 14,
                    padding: '12px 18px',
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    color: 'var(--cpm-text-primary)',
                    fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                }}
            >
                <CpmCheckbox
                    checked={allSelected}
                    onChange={(e) => onToggleAll(e.target.checked)}
                />
                <span>Barcode / GTIN</span>
                <span>Claiming master products</span>
                <span>Reporting store brands</span>
                <span>First conflict / drop-off</span>
                <span />
            </div>
            {items.map((row) => {
                const selected = selectedIds.includes(row.barcode);
                const view = deriveBarcodeView(row);
                return (
                    <div
                        key={row.barcode}
                        className={[
                            'grid items-center border-t border-[var(--cpm-border-default)] cursor-pointer hover:bg-[var(--cpm-surface-subtle)]',
                            selected ? 'bg-[var(--cpm-purple-tint-3)]' : '',
                        ].join(' ')}
                        style={{
                            gridTemplateColumns:
                                '38px minmax(150px,0.8fr) minmax(0,1.4fr) minmax(0,1.4fr) 152px 26px',
                            gap: 14,
                            padding: '15px 18px',
                        }}
                        onClick={() => onOpen(row)}
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <CpmCheckbox
                                checked={selected}
                                onChange={() => onToggle(row)}
                            />
                        </div>
                        <div
                            className="flex flex-col min-w-0 overflow-hidden"
                            style={{ gap: 6 }}
                        >
                            <span
                                className="min-w-0 truncate text-[var(--cpm-text-primary)] font-[family-name:var(--cpm-font-mono)] tabular"
                                style={{
                                    fontSize: 15,
                                    fontWeight: 800,
                                    letterSpacing: '0.02em',
                                }}
                            >
                                {row.barcode}
                            </span>
                            {view.badges.length > 0 ? (
                                <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
                                    {view.badges.map((b) => (
                                        <span
                                            key={b.label}
                                            title={b.hint}
                                            className={[
                                                'shrink-0 inline-flex items-center px-[9px] py-[3px] rounded-full border',
                                                b.kind === 'conflict'
                                                    ? 'bg-[var(--cpm-shortfall-bg)] border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]'
                                                    : 'bg-[var(--cpm-warning-bg)] border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]',
                                            ].join(' ')}
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.04em',
                                            }}
                                        >
                                            {b.label}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
                            <span
                                className="tabular"
                                style={{
                                    fontSize: 13.5,
                                    fontWeight: 800,
                                    color: view.conflict
                                        ? 'var(--cpm-shortfall-text-med)'
                                        : 'var(--cpm-success-text-light)',
                                }}
                            >
                                {view.claims}
                            </span>
                            <CpmTooltip content={numberedTooltipList(view.claimNamesList)}>
                                <span
                                    className="block w-full truncate text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 11.5, fontWeight: 600 }}
                                >
                                    {view.claimNames || '—'}
                                </span>
                            </CpmTooltip>
                        </div>
                        <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
                            <CpmTooltip
                                content={
                                    view.reporterNamesList.length > 0 ? (
                                        <div className="flex flex-col" style={{ gap: 6 }}>
                                            <span>
                                                {view.reporterNamesList.length} store brand
                                                {view.reporterNamesList.length === 1 ? '' : 's'}
                                            </span>
                                            {numberedTooltipList(view.reporterNamesList)}
                                        </div>
                                    ) : null
                                }
                            >
                                <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
                                    <span
                                        className="block w-full truncate text-[var(--cpm-text-primary)] tabular"
                                        style={{ fontSize: 13.5, fontWeight: 800 }}
                                    >
                                        {view.reports}
                                    </span>
                                    <span
                                        className="block w-full truncate text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 11.5, fontWeight: 600 }}
                                    >
                                        {view.reportNames}
                                    </span>
                                </div>
                            </CpmTooltip>
                            {view.hasDropped ? (
                                <span
                                    className="inline-flex items-center truncate text-[var(--cpm-warning-text)]"
                                    style={{ gap: 6, fontSize: 11.5, fontWeight: 800 }}
                                >
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        className="shrink-0"
                                    >
                                        <path d="M22 17 13.5 8.5 8.5 13.5 2 7" />
                                        <path d="M16 17h6v-6" />
                                    </svg>
                                    {view.droppedLabel}
                                </span>
                            ) : null}
                        </div>
                        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                            <span
                                className="text-[var(--cpm-text-secondary)] tabular"
                                style={{ fontSize: 12.5, fontWeight: 700 }}
                            >
                                {formatFlaggedDate(row.firstConflictAt)}
                            </span>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '0.05em',
                                }}
                            >
                                {view.sinceLabel}
                            </span>
                        </div>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--cpm-text-tertiary)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="m9 18 6-6-6-6" />
                        </svg>
                    </div>
                );
            })}
        </div>
    );
}
